import React, { useState, useCallback, useEffect } from 'react';
import { DatePicker, Button, Select, Card, message, Table, Spin, Col, Row, Form } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_TXALERT, API_RETAILER } from '../../config/config';
import htmlParser from 'html-react-parser';

const { RangePicker } = DatePicker;

const RETAILER_DATA_URL = API_RETAILER + '/list';

const AlertResolutionReport = () => {
  const [dateRange, setDateRange] = useState([
    moment().startOf('month'),
    moment().endOf('month')
  ]);
  const [retailer, setRetailer] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [retailerList, setRetailerList] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [total, setTotal] = useState(0);

  // Fetch retailer list (scoped server-side to the user's associations)
  const getRetailerList = useCallback(() => {
    axios
      .post(
        RETAILER_DATA_URL,
        {},
        { headers: { Authorization: 'Bearer ' + Cookies.get('access_token'), Authuser: Cookies.get('authuser') } }
      )
      .then(({ data }) => {
        setRetailerList(data.list || []);
      })
      .catch(() => false);
  }, []);

  useEffect(() => {
    getRetailerList();
  }, [getRetailerList]);

  // Default to all retailers when multiple are available; single retailer is implicit (selector hidden)
  const hasMultipleRetailers = retailerList.length > 1;

  // Shared guard: valid range + 1-month cap (enforced for both preview and download)
  const validateDateRange = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('Please select a valid date range');
      return false;
    }
    const monthsDiff = dateRange[1].diff(dateRange[0], 'months', true);
    if (monthsDiff > 1) {
      message.error('Date range cannot exceed 1 month');
      return false;
    }
    return true;
  };

  const buildPayload = (preview, page = 1, size = 20) => {
    const payload = {
      date_from: dateRange[0].format('YYYY-MM-DD'),
      date_to: dateRange[1].format('YYYY-MM-DD'),
      report_type: 'alert_resolution_report',
      preview: preview,
      // Preview paginates server-side; export pulls the full result set in one file
      page: preview ? page : 1,
      size: preview ? size : 999999
    };
    if (!preview) {
      payload.export_filetype = 'xlsx';
    }
    // Only send retailer when the user actively selected one (multiple available)
    if (hasMultipleRetailers && retailer.length > 0) {
      payload.rid = retailer;
    }
    return payload;
  };

  // Fetch a single page of preview data from the server
  const fetchPreview = async (page, pageSize) => {
    setLoading(true);
    try {
      const response = await axios.post(
        API_TXALERT + '/reports',
        buildPayload(true, page, pageSize),
        {
          headers: {
            Authorization: 'Bearer ' + Cookies.get('access_token'),
            Authuser: Cookies.get('authuser')
          }
        }
      );

      if (response.data && response.data.data) {
        setReportData(response.data.data);
        setTotal(response.data.count || 0);
        setPagination({ current: page, pageSize });
        message.success(`Report preview loaded: ${response.data.count || 0} rows`);
      } else {
        setReportData(null);
        setTotal(0);
        message.info('No alerts found for the selected date range and filters.');
      }
    } catch (error) {
      console.error('Preview generation error:', error);
      if (error.response && error.response.status === 408) {
        message.error('Report generation is taking longer than expected. Please try with a smaller date range.');
      } else {
        message.error('Failed to generate preview: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Pager change refetches the requested page from the server
  const handlePaginationChange = (page, pageSize) => {
    fetchPreview(page, pageSize);
  };

  const handleGeneratePreview = () => {
    if (!validateDateRange()) {
      return;
    }
    setReportData(null);
    setTotal(0);
    fetchPreview(1, pagination.pageSize);
  };

  const handleDownloadXLSX = async () => {
    if (!validateDateRange()) {
      return;
    }

    setDownloading(true);

    try {
      const response = await axios.post(
        API_TXALERT + '/reports',
        buildPayload(false),
        {
          headers: {
            Authorization: 'Bearer ' + Cookies.get('access_token'),
            Authuser: Cookies.get('authuser')
          }
        }
      );

      if (response.data && response.data.data) {
        const link = document.createElement('a');
        link.href = response.data.data;
        link.download = `alert_resolution_report_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('Report downloaded successfully');
      }
    } catch (error) {
      console.error('Download error:', error);
      message.error('Failed to download report: ' + (error.message || 'Unknown error'));
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    { title: 'Alert Date', dataIndex: 'alert_date', key: 'alert_date', width: 120 },
    { title: 'Alert Reason', dataIndex: 'alert_reason', key: 'alert_reason', width: 150 },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 100, align: 'right' },
    { title: 'Auth Code', dataIndex: 'auth_code', key: 'auth_code', width: 100 },
    { title: 'Card No.', dataIndex: 'card_number', key: 'card_number', width: 150 },
    { title: 'Condition Code', dataIndex: 'condition_code', key: 'condition_code', width: 120 },
    { title: 'Descriptor', dataIndex: 'descriptor', key: 'descriptor', width: 200 },
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Merchant Update On', dataIndex: 'merchant_update_on', key: 'merchant_update_on', width: 160 },
    { title: 'Outcome', dataIndex: 'outcome', key: 'outcome', width: 120 },
    { title: 'Provider', dataIndex: 'provider', key: 'provider', width: 150 },
    { title: 'Transaction Date', dataIndex: 'transaction_date', key: 'transaction_date', width: 140 },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 120 },
    { title: 'Updated By', dataIndex: 'updated_by', key: 'updated_by', width: 120 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes', width: 300 },
    { title: 'Merchant Transaction ID', dataIndex: 'merchant_transaction_id', key: 'merchant_transaction_id', width: 200 }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Alert Resolution Report" style={{ marginBottom: 16 }}>
        <Form size="large">
          <Row gutter={[16, 16]}>
            {/* Date Range Picker */}
            <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }} xl={{ span: 8 }}>
              <Form.Item>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Date Range <span style={{ color: 'red' }}>*</span>
                </label>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="YYYY-MM-DD"
                  style={{ width: '100%' }}
                  placeholder={['Start Date', 'End Date']}
                />
                <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                  Maximum 1 month
                </div>
              </Form.Item>
            </Col>

            {/* Retailer Filter — hidden when only one retailer is available */}
            {hasMultipleRetailers && (
              <Col className="gutter-row" xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 12 }} lg={{ span: 8 }} xl={{ span: 8 }}>
                <Form.Item>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Retailer
                  </label>
                  <Select
                    showSearch
                    mode="multiple"
                    showArrow={true}
                    maxTagCount="responsive"
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    onChange={setRetailer}
                    value={retailer}
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                    filterSort={(optionA, optionB) => optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())}
                    placeholder="All retailers"
                    allowClear
                  >
                    {retailerList.map((item) => (
                      <Select.Option key={item.rid} value={item.rid}>
                        {htmlParser(item.name) + ` (rid: ` + item.rid + `)`}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}

          </Row>

          {/* Action Buttons — right-aligned on their own row below the filters */}
          <Row gutter={[16, 16]} justify="end">
            {reportData && (
              <Col>
                <Button
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadXLSX}
                  loading={downloading}
                >
                  Download XLSX
                </Button>
              </Col>
            )}
            <Col>
              <Button
                type="primary"
                icon={<EyeOutlined />}
                onClick={handleGeneratePreview}
                loading={loading}
              >
                Generate Report Preview
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, fontSize: 16 }}>Loading report data...</div>
          </div>
        </Card>
      )}

      {/* Report Preview */}
      {reportData && !loading && (
        <Card>
          <Table
            columns={columns}
            dataSource={reportData}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (t) => `Total ${t} rows`,
              onChange: handlePaginationChange,
              onShowSizeChange: handlePaginationChange
            }}
            scroll={{ x: 'max-content' }}
            size="small"
            bordered
            rowKey={(record, index) => record.id ?? index}
          />
        </Card>
      )}
    </div>
  );
};

export default AlertResolutionReport;
